import React from 'react';

import {
  WorksheetSeedDataConfigEditor,
  isWorksheetSeedDataConfigValid,
} from 'notebook/worksheets/components/WorksheetAnalysis';
import {
  validateProcedureRunSeedDataConfigValid,
  ProcedureRunSeedDataConfigEditor,
} from 'studies/typed/shared/analysis/ProcedureRunAnalysis';
import {
  validateStudySeedDataConfigValid,
  StudySeedDataConfigEditor,
} from 'studies/typed/shared/analysis/StudyAnalysis';
import FormGroup from 'ui-library/bs/FormGroup';
import lazyInit from 'util/lazyInit';

import {ExampleSeedDataConfigEditor, validateExampleSeedDataConfig} from './ExampleSeedDataConfigEditor';
import {SeedDataConfig, SeedDataConfigType} from './SeedDataTypes';

interface SeedDataConfigMeta<T extends SeedDataConfigType> {
  component: React.FC<{
    value: SeedDataConfig & {type: T};
    onChange: (value: SeedDataConfig & {type: T}) => void;
  }>;
  validate: (config: SeedDataConfig & {type: T}) => boolean;
}

/**
 * Component registry for each SeedDataConfigType.
 *
 * NOTE: We use lazyInit to ensure we don't run into issues with circular dependencies.
 */
const getSeedDataConfigTypeMap = lazyInit<
  Record<SeedDataConfigType, SeedDataConfigMeta<SeedDataConfigType>> & {
    [K in SeedDataConfigType]?: SeedDataConfigMeta<K>;
  }
>(() => ({
  [SeedDataConfigType.EXAMPLE_SEED_DATA]: {
    component: ExampleSeedDataConfigEditor,
    validate: validateExampleSeedDataConfig,
  },
  [SeedDataConfigType.PROCEDURE_RUN]: {
    component: ProcedureRunSeedDataConfigEditor,
    validate: validateProcedureRunSeedDataConfigValid,
  },
  [SeedDataConfigType.STUDY]: {
    component: StudySeedDataConfigEditor,
    validate: validateStudySeedDataConfigValid,
  },
  [SeedDataConfigType.WORKSHEET]: {
    component: WorksheetSeedDataConfigEditor,
    validate: isWorksheetSeedDataConfigValid,
  },
}));

export function isSeedDataConfigValid(seedDataConfig: SeedDataConfig): boolean {
  const {validate} = getSeedDataConfigTypeMap()[seedDataConfig.type];
  return validate(seedDataConfig);
}

interface SeedDataRenderProps {
  seedDataConfig: SeedDataConfig;
  setSeedDataConfig: (newSeedDataConfig: SeedDataConfig) => void;
}

export default function SeedDataRender({
  seedDataConfig,
  setSeedDataConfig,
}: SeedDataRenderProps): JSX.Element {
  const {component: Component} = getSeedDataConfigTypeMap()[seedDataConfig.type];
  return (
    <FormGroup>
      <Component value={seedDataConfig} onChange={setSeedDataConfig} />
    </FormGroup>
  );
}
